<script lang="ts">
import { ThemeType } from '@goodlifeplus/themes'
import { type PropType, defineComponent } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

export type HeaderBanner = {
  /**
   * The variant of the header banner.
   * - 'image': Left side fixed width.
   * - 'clipped': Right side fixed width.
   * - 'unclipped': Background absolutely, right positioned with left side expanding width.
   */
  variant: 'image' | 'clipped' | 'unclipped'
  badge_text?: string
  /** The main text displayed on the banner. Can include HTML for formatting. */
  text: string
  /** The subtext displayed below the main text. Can include HTML for formatting. */
  subtext?: string
  cta_text: string
  show_trustpilot: boolean
  show_logo: boolean
  squiggle_color?: 'purple' | 'red' | 'green' | 'gold' | 'blue'
  route: RouteLocationRaw & { class?: string }
  image_url: string
}

/**
 * HeaderBannerBaseCard component displays a banner with different layout variants.
 */
export default defineComponent({
  props: {
    banner: {
      type: Object as PropType<HeaderBanner>,
      required: true,
    },
  },
  setup() {
    return {
      ThemeType,
    }
  },
})
</script>

<template>
  <RouterLink
    v-if="banner.route"
    :to="banner.route"
    class="header-banner mask-squiggle before:max-w-[525px] min-[455px]:before:max-w-[455px] min-[640px]:before:max-w-[525px] before:[mask-size:cover]"
    :class="banner.squiggle_color"
    :style="{
      '--mask-squiggle-url': `url('/assets/images/masks/squiggle-lg.svg')`,
      ...(banner.squiggle_color
        ? { '--mask-background': `var(--gradient-${banner.squiggle_color})` }
        : {}),
    }"
  >
    <div class="header-banner__content">
      <div v-if="banner.badge_text" class="header-banner__badge">
        <AppBadge
          size="md"
          class="text-pure-white bg-primary !p-2 uppercase leading-none !rounded-md"
        >
          {{ banner.badge_text }}
        </AppBadge>
      </div>

      <img
        v-if="banner.show_logo"
        src="@/assets/images/partners/glp-logo.png"
        alt="GLP Logo"
        class="h-4 w-[120px]"
      />

      <h1
        class="header-banner__text"
        :class="{ 'font-club': $theme.type === ThemeType.Club }"
        v-html="banner.text"
      />

      <p
        v-if="banner.subtext"
        class="header-banner__subtext"
        v-html="banner.subtext"
      ></p>

      <AppButton
        :title="banner.cta_text"
        class="button-primary header-banner__cta"
        :class="banner.route.class ?? ''"
      >
        {{ banner.cta_text }}
      </AppButton>

      <div
        v-if="banner.show_trustpilot && banner.variant !== 'image'"
        class="header-banner__trustpilot"
      >
        <AppTrustpilot />
      </div>
    </div>

    <div class="header-banner__image">
      <img
        :src="banner.image_url"
        alt="Header Banner Image"
        class="object-cover object-center size-full"
      />

      <div
        v-if="banner.show_trustpilot && banner.variant === 'image'"
        class="header-banner__trustpilot"
      >
        <AppTrustpilot size="sm" />
      </div>
    </div>
  </RouterLink>
</template>

<style lang="scss">
:root {
  --header-banner-height: 240px;
  --header-banner-static-section-width: 178px;
  // --card-max-width-breakpoint: 20em;
}
</style>

<style lang="scss" scoped>
.header-banner {
  @apply bg-center bg-cover overflow-hidden flex size-full border-none @container hover:cursor-pointer !h-[var(--header-banner-height)] @sm/main:rounded-lg text-pure-white relative bg-no-repeat bg-[url('@/assets/images/banners/texture-blue.webp')];

  .header-banner__content {
    @apply flex flex-col justify-between h-full z-[1] w-44 @[20em]:w-[calc(100%-var(--header-banner-static-section-width)+10px)] @[20em]:gap-4;
  }

  .header-banner__text {
    @apply font-black;
  }

  .header-banner__subtext {
    @apply font-black;
  }

  .header-banner__image {
    // Height of 101% to prevent image gap at bottom due to subpixel rendering
    @apply relative overflow-hidden h-[101%];
  }

  &:deep(.header-banner__cta) {
    @apply h-auto py-2 px-4 max-w-[var(--header-banner-static-section-width)] whitespace-nowrap leading-none capitalize font-bold border-none text-sm @[20em]:mt-auto !important;
  }

  .header-banner__trustpilot {
    @apply w-fit;
  }
}
</style>
